import React, { useMemo } from 'react';
import dayjs from 'dayjs';
import styled from 'styled-components';
import { EuiToolTip } from '@elastic/eui';

const MainWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 100%;
  overflow: hidden;
`;

const WrapperStyled = styled.div`
  display: flex;
  align-items: stretch;
  height: ${ props => props.isBig ? '42px' : '16px' };
  z-index: 10;
  position: relative;
  gap: ${ props => props.isBig ? '4px' : '2px' };
  width: 100%;
  max-width: 100%;

  @media (max-width: 600px) {
    gap: ${ props => props.isBig ? '2px' : '1px' };
    height: ${ props => props.isBig ? '28px' : '12px' };
  }

  > * {
    flex: 1;
    display: flex;
    align-items: stretch;
    height: 100%;
    min-width: ${ props => props.isBig ? '5px' : '2px' };
    
    @media (max-width: 600px) {
      min-width: ${ props => props.isBig ? '2px' : '1px' };
    }
  }

  .euiToolTipAnchor {
    flex: 1;
    display: flex;
    align-items: stretch;
    height: 100%;
    min-width: ${ props => props.isBig ? '5px' : '2px' };
    
    @media (max-width: 600px) {
      min-width: ${ props => props.isBig ? '2px' : '1px' };
    }
  }
`;

const ItemStyled = styled.div`
  background: #50bc5e;
  border-radius: ${ props => props.isBig ? '3px' : '1px' };
  cursor: ${ props => props.isBig ? 'pointer' : 'default' };
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);

  ${ props => props.isBig ? `
    width: 100%;
    height: 100%;
  ` : `
    flex: 1;
    min-width: 2px;
    height: 100%;
  ` }

  &.pending {
    background: rgba(255, 255, 255, 0.12);
    box-shadow: none;
  }

  &.ok {
    background: #50bc5e;
    box-shadow: 0 1px 3px rgba(80, 188, 94, 0.2);
  }

  &.fail {
    background: #F86B63;
    box-shadow: 0 1px 3px rgba(248, 107, 99, 0.2);
  }

  &:hover {
    ${ props => props.isBig && `
      transform: scaleY(1.15) scaleX(1.05);
      filter: brightness(1.25);
      box-shadow: 0 4px 10px rgba(0, 0, 0, 0.3);
      z-index: 12;
    ` }
  }
`;

const TooltipContent = styled.div`
  padding: 6px;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
  
  .time {
    font-size: 10px;
    color: rgba(255, 255, 255, 0.5);
    margin-bottom: 4px;
  }
  
  .status {
    font-size: 12px;
    font-weight: 600;
    display: flex;
    align-items: center;
    gap: 6px;
  }
  
  .meta {
    font-size: 11px;
    color: rgba(255, 255, 255, 0.85);
    margin-top: 4px;
    font-family: Consolas, Monaco, monospace;
  }
`;

const TimelineLabels = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 8px;
  font-size: 11px;
  color: rgba(255, 255, 255, 0.45);
  font-weight: 500;
`;

const MiniPinyWidget = ({ uptime, uptimeChecks, isBig }) => {
    const checks = (uptimeChecks && uptimeChecks.length > 0) ? uptimeChecks : (uptime.uptimechecks || []);
    let firstCheck = null;
    if (checks && checks.length > 0) {
        firstCheck = checks[0];
    }

    const generatedChecks = useMemo(() => {
        return generateChecks(checks, uptime, isBig ? 30 : 15);
    }, [uptime, checks, firstCheck, isBig]);

    if (generatedChecks.length === 0) {
        return (
            <div style={{ color: 'rgba(255, 255, 255, 0.4)', fontSize: '12px', padding: '10px 0', textAlign: 'center' }}>
                No checks recorded yet. Activate the monitor to start checks.
            </div>
        );
    }

    return (
        <MainWrapper>
            <WrapperStyled isBig={ isBig }>
                { generatedChecks.map((item, idx) => {
                    return (
                        <Item
                            key={ item.date + idx }
                            item={ item }
                            isBig={ isBig }
                        />);
                }) }
            </WrapperStyled>
            { isBig && generatedChecks.length > 0 && (
                <TimelineLabels>
                    <span>{ generatedChecks.filter(c => c.status !== 'pending').length } checks ago</span>
                    <span>Just now</span>
                </TimelineLabels>
            ) }
        </MainWrapper>
    );
};

export default MiniPinyWidget;

export const generateChecks = (checks, uptime, count = 15) => {
    const results = [];
    let interval = uptime.interval;
    if (uptime.isCustomInterval) {
        interval = uptime.customInterval;
    }

    const nextRun = uptime.scheduleTo;

    if (Array.isArray(checks)) {
        const slicedChecks = checks.length > count ? checks.slice(-count) : checks;
        slicedChecks.forEach(check => {
            results.push({
                status: check.isValid ? 'ok' : 'fail',
                date: dayjs(check.createdAt).format('HH:mm DD MMM YYYY'),
                responseTime: check.responseTime,
                statusCode: check.actualStatus,
                message: check.message
            });
        });

        const checksLength = slicedChecks.length;
        if (checksLength < count) {
            if(uptime.isEnabled){
                const counterToAddMoreChecks = count - checksLength;
                for (let j = 0; j < counterToAddMoreChecks; j++) {
                    let dateMS = j === 0 ? nextRun : nextRun + (interval * 1000 * j);
                    results.push({
                        status: 'pending',
                        date: dayjs(dateMS).format('HH:mm DD MMM YYYY')
                    });
                }
            }
        } else {
            results.push({
                status: 'pending',
                date: dayjs(nextRun).format('HH:mm DD MMM YYYY')
            });
        }
    } else {
        for (let i = 0; i < count; i++) {
            let dateMS = i === 0 ? nextRun : nextRun + (interval * 1000 * i);
            results.push({
                status: 'pending',
                date: dayjs(dateMS).format('HH:mm DD MMM YYYY')
            });
        }
    }

    return results;
};

const Item = ({ item, isBig }) => {
    if (!isBig) {
        return (
            <ItemStyled
                className={ item.status }
                isBig={ isBig }
            />
        );
    }

    const isOk = item.status === 'ok';
    const isPending = item.status === 'pending';
    
    let statusText = 'Online';
    let statusColor = '#50bc5e';
    
    if (isPending) {
        statusText = 'Pending Check';
        statusColor = 'rgba(255, 255, 255, 0.45)';
    } else if (!isOk) {
        statusText = 'Offline';
        statusColor = '#F86B63';
    }

    return (
        <EuiToolTip
            position="top"
            delay="fast"
            content={
                <TooltipContent>
                    <div className="time">{ item.date }</div>
                    <div className="status" style={{ color: statusColor }}>
                        <span style={{
                            display: 'inline-block',
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            background: statusColor,
                            boxShadow: isPending ? 'none' : `0 0 6px ${statusColor}`
                        }} />
                        { statusText } { item.statusCode ? `(${item.statusCode})` : '' }
                    </div>
                    { !isPending && item.responseTime !== undefined && (
                        <div className="meta">
                            Latency: { item.responseTime } ms
                        </div>
                    ) }
                    { !isPending && item.message && item.message !== 'null' && (
                        <div className="meta" style={{ color: '#F86B63', fontSize: '10.5px', maxWidth: '200px', wordBreak: 'break-word' }}>
                            { item.message }
                        </div>
                    ) }
                </TooltipContent>
            }
        >
            <ItemStyled
                className={ item.status }
                isBig={ isBig }
            />
        </EuiToolTip>
    );
};
